import { useState } from 'react';

import { jobsConfigApi, type TenantJobRun } from '@/api/queries/jobsConfig';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { formatDuration, formatTotals, runStatusTone } from '@/components/jobs/runUtils';
import { Modal, Pagination, StatusPill, Table, type TableColumn } from '@/components/ui';
import { formatDateTime } from '@/utils/formatDate';

/**
 * Run-history tab of the tenant job detail page (/settings/jobs/$key). Shows the
 * tenant's slice of each global JobRun (counters / note for this organisation
 * only); row click opens an overview detail modal.
 */
export function TenantJobRuns({ jobKey }: { jobKey: string }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const { data, isPending } = jobsConfigApi.useTenantJobRuns(jobKey, {
    from: page * pageSize,
    count: pageSize,
  });

  const columns: TableColumn<TenantJobRun>[] = [
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <StatusPill status={runStatusTone(r.status)}>{r.status}</StatusPill>,
      width: 120,
    },
    { id: 'started', header: 'Started', cell: (r) => formatDateTime(r.startedAt) ?? '—', width: 180 },
    { id: 'duration', header: 'Duration', cell: (r) => formatDuration(r.durationMs), width: 110 },
    { id: 'trigger', header: 'Trigger', cell: (r) => r.triggeredBy, width: 100 },
    {
      id: 'changes',
      header: 'Changes',
      cell: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{formatTotals(r.counters)}</span>,
    },
    {
      id: 'note',
      header: 'Note',
      cell: (r) => (r.note ? <span style={{ color: 'var(--text-3)' }}>{r.note}</span> : '—'),
      width: 180,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Table
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(r) => r.id}
        loading={isPending}
        emptyMessage="No runs recorded yet."
        onRowClick={(r) => setOpenRunId(r.id)}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(0);
          setPageSize(size);
        }}
      />
      {openRunId && <TenantRunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </div>
  );
}

function TenantRunDetailModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data: run, isPending } = jobsConfigApi.useTenantJobRun(runId);

  return (
    <Modal open onClose={onClose} title="Run detail" size="lg">
      {isPending || !run ? (
        <LoadingSpinner />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Meta label="Status">
              <StatusPill status={runStatusTone(run.status)}>{run.status}</StatusPill>
            </Meta>
            <Meta label="Trigger">{run.triggeredBy}</Meta>
            <Meta label="Started">{formatDateTime(run.startedAt) ?? '—'}</Meta>
            <Meta label="Finished">{formatDateTime(run.finishedAt) ?? '—'}</Meta>
            <Meta label="Duration">{formatDuration(run.durationMs)}</Meta>
          </div>

          {run.error && (
            <div
              style={{
                padding: 12,
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-2)',
                color: 'var(--red, var(--text-2))',
                fontSize: 13,
              }}
            >
              {run.error}
            </div>
          )}

          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>
              Changes for your organisation
            </h3>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatTotals(run.counters)}</div>
            {run.note && (
              <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 6 }}>{run.note}</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text-1)' }}>{children}</span>
    </div>
  );
}
